import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useScheduledSessionsFilter from '@/services/useScheduledSessionsFilter'
import { Popover } from 'radix-ui'
import { PortalContext } from '@/UIComponents2/PortalContext'
import { Button } from '@/UIComponents2/Button'
import CalendarIcon from '@/CustomIcons/CalendarIcon'
import HistoryIcon from '@/CustomIcons/HistoryIcon'
import UserIcon from '@/CustomIcons/UserIcon'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import RadioCircleIcon from '@/CustomIcons/RadioCircleIcon'
import { IconCircle } from '@/components/CircleIcon'
import { DateTime } from 'luxon'
import { useHistory } from 'react-router-dom'
import { cn } from '@/utlis/cn'
import { DashboardRoom, ScheduledSessionOfUser } from '@/AppContext'
import CalendarMonthIcon from '@/CustomIcons/CalendarMonthIcon'
import ClearIcon from '@/CustomIcons/ClearIcon'
import { persistent } from '@/services/LocalStorage'

export default function MobileSessionsMenu() {
    const portalContainer = useContext(PortalContext)
    const { t } = useTranslation()

    const scheduledSessions = useMemo(() => {
        return persistent.getArray<ScheduledSessionOfUser>('scheduledSessions') || []
    }, [])
    const permanentRooms = useMemo(() => {
        return persistent.getArray<DashboardRoom>('permanentRooms') || []
    }, [])
    const recentRooms = useMemo(() => {
        return persistent.getArray<DashboardRoom>('recentRooms') || []
    }, [])

    const scheduledSessionsFilter = useScheduledSessionsFilter(scheduledSessions)

    const sessionElements = scheduledSessionsFilter.sessionsInWindow.map((session) => (
        <div className="flex flex-col gap-2" key={session.joinCode}>
            <div className="h-[1px] bg-[rgba(255,255,255,0.08)] w-full" />
            <MobileSessionCard key={session.joinCode} session={session} />
        </div>
    ))

    const recentRoomsElements = recentRooms.map((room) => (
        <div className="flex flex-col gap-2" key={room.joinCode}>
            <div className="h-[1px] bg-[rgba(255,255,255,0.08)] w-full" />
            <MobileRoomCard key={room.joinCode} room={room} />
        </div>
    ))

    const permanentRoomsElements = permanentRooms.map((room) => (
        <div className="flex flex-col gap-2" key={room.joinCode}>
            <div className="h-[1px] bg-[rgba(255,255,255,0.08)] w-full" />
            <MobileRoomCard key={room.joinCode} room={room} />
        </div>
    ))

    return (
        <Popover.Root>
            <Popover.Trigger asChild className="group">
                <Button>
                    <div className="group-data-[state=open]:hidden">
                        <CalendarMonthIcon />
                    </div>
                    <div className="hidden group-data-[state=open]:block">
                        <ClearIcon />
                    </div>
                </Button>
            </Popover.Trigger>
            <Popover.Portal container={portalContainer}>
                <Popover.Content
                    sideOffset={10}
                    align="end"
                    className="popover-content md:hidden w-[calc(100vw-10px)] max-w-[500px] h-[calc(var(--radix-popover-content-available-height)-10px)] bg-neutral-700/70 backdrop-blur-[50px] z-30 rounded-b-[28px] rounded-t-[28px] p-1 py-3 overflow-hidden"
                >
                    <div className="overflow-y-auto max-h-full">
                        <div className="flex flex-col">
                            <div className="t-lg-medium p-2 flex items-center gap-2">
                                <CalendarIcon />
                                {scheduledSessionsFilter.windowLabel}
                            </div>
                            <div className="flex flex-col gap-2">{sessionElements}</div>
                        </div>
                        <div className="flex flex-col pt-1">
                            <div className="t-lg-medium p-2 flex items-center gap-2">
                                <HistoryIcon style={{ fontSize: '20px', width: '24px' }} />
                                {t('History')}
                            </div>
                            <div className="flex flex-col gap-2">{recentRoomsElements}</div>
                        </div>
                        <div className="flex flex-col pt-1 pb-1">
                            <div className="t-lg-medium p-2 flex items-center gap-2">
                                <UserIcon />
                                {t('Room')}
                            </div>
                            <div className="flex flex-col gap-2">{permanentRoomsElements}</div>
                        </div>
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    )
}

function MobileSessionCard({ session }: { session: ScheduledSessionOfUser }) {
    const start = DateTime.fromISO(session.scheduledStartTime)
    const end = DateTime.fromISO(session.scheduledEndTime)
    const active = isActive(start, end)
    const history = useHistory()

    return (
        <div className="flex flex-col px-3">
            <div className="flex justify-between items-center w-full gap-2 min-w-[275px]">
                <div>
                    <IconCircle className="relative w-[40px] h-[40px]">
                        <RadioCircleIcon className={cn(active && 'text-[#85E0A3]')} style={{ fontSize: '20px' }} />
                    </IconCircle>
                </div>
                <div className="flex flex-col overflow-hidden flex-1">
                    <span className="t-base-medium truncate">{session.name}</span>
                    <span className="t-base-light opacity-50">
                        {start.toFormat('LLL dd')}, {start.toFormat('h:mm')} - {end.toFormat('h:mm')}{' '}
                        {end.toFormat('a').toLowerCase()}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant={active ? 'glow' : 'secondary'}
                        className="py-0 px-0 w-[48px] h-[40px]"
                        onClick={() => {
                            history.push(`/join?code=${session.joinCode}`)
                        }}
                    >
                        <RightArrowIcon />
                    </Button>
                </div>
            </div>
        </div>
    )
}

function MobileRoomCard({ room }: { room: DashboardRoom }) {
    const history = useHistory()

    return (
        <div className="flex flex-col px-3">
            <div className="flex justify-between items-center w-full gap-2 min-w-[275px]">
                <div>
                    <IconCircle className="relative w-[40px] h-[40px]">
                        <RadioCircleIcon style={{ fontSize: '20px' }} />
                    </IconCircle>
                </div>
                <div className="flex flex-col overflow-hidden flex-1 min-w-0">
                    <span className="t-base-medium truncate">{room.name}</span>
                    <span className="t-base-light opacity-50">
                        Active {DateTime.fromISO(room.updatedAt ?? '').toRelative()}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant={'secondary'}
                        className="py-0 px-0 w-[48px] h-[40px]"
                        onClick={() => {
                            history.push(`/join?code=${room.joinCode}`)
                        }}
                    >
                        <RightArrowIcon />
                    </Button>
                </div>
            </div>
        </div>
    )
}

function isActive(start: DateTime, end: DateTime) {
    const startDiffMinutes = start.diffNow().as('minutes')
    const endDiffMinutes = end.diffNow().as('minutes')

    const started = startDiffMinutes < 5 // starts within 5 minutes
    const ended = endDiffMinutes < -5 // ended within the last 5 minutes

    return started && !ended
}
